import { ratioOf } from "./ratioCalc.js";

// Missing observations are not measured zero. Keep each additive metric's
// completeness independent so a missing revenue does not invalidate spend.
export const finiteMetric = value => (typeof value === "number" || typeof value === "string") && String(value).trim() !== "" && Number.isFinite(Number(value)) ? Number(value) : null;
export const emptyPerformanceTotals = () => ({ spend: 0, rev: 0, cv: 0, click: 0, imp: 0 });
export const basePerformanceKeys = Object.keys(emptyPerformanceTotals());
const field = (row, short, full) => Object.hasOwn(row, short) ? row[short] : row[full];
export function performanceValues(row) {
  return { spend: row._costUnavailable ? null : finiteMetric(row.spend), rev: finiteMetric(field(row, "rev", "revenue")),
    cv: finiteMetric(row.cv), click: finiteMetric(field(row, "click", "clicks")), imp: finiteMetric(field(row, "imp", "impressions")) };
}
export function addPerformanceTotals(totals, values) {
  for (const key of basePerformanceKeys) totals[key] = totals[key] === null || values[key] === null ? null : finiteMetric(totals[key] + values[key]);
  return totals;
}
const operands = { roas: ["rev", "spend"], cpa: ["spend", "cv"], ctr: ["click", "imp"],
  cvr: ["cv", "click"], cpc: ["spend", "click"], cpm: ["spend", "imp"], aov: ["rev", "cv"] };
export function performanceRatio(totals, metric) {
  if (!operands[metric]?.every(key => Number.isFinite(totals[key]))) return null;
  return finiteMetric(ratioOf(totals, metric));
}
export function aggregatePerformance(rows = []) {
  const totals = rows.reduce((sum, row) => addPerformanceTotals(sum, performanceValues(row)), emptyPerformanceTotals());
  if (!rows.length) for (const key of basePerformanceKeys) totals[key] = null;
  for (const metric of Object.keys(operands)) {
    const value = performanceRatio(totals, metric);
    totals[metric] = value !== null && ["cpc", "cpm", "cpa"].includes(metric) ? Math.round(value) : value;
  }
  return totals;
}
export function dailyPerformance(rows = []) {
  const groups = new Map();
  for (const row of rows) {
    if (!row.date) continue;
    const date = String(row.date).slice(0, 10);
    if (!groups.has(date)) groups.set(date, []);
    groups.get(date).push(row);
  }
  return [...groups].sort(([a], [b]) => a.localeCompare(b)).map(([date, entries]) => ({ date, count: entries.length, ...aggregatePerformance(entries) }));
}
export function comparisonDailyPerformance(current, previous = []) {
  const rows = new Map(dailyPerformance(current).map(row => [row.date, row]));
  for (const row of dailyPerformance(previous)) {
    if (!rows.has(row.date)) rows.set(row.date, { date: row.date, count: 0, ...aggregatePerformance() });
    const target = rows.get(row.date);
    for (const key of ["spend", "cv", "rev", "roas"]) target[key + "B"] = row[key];
  }
  return [...rows.values()].sort((a, b) => a.date.localeCompare(b.date));
}
export function performanceChange(current, previous, points = false) {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  return finiteMetric(points ? current - previous : previous > 0 ? (current - previous) / previous * 100 : null);
}

export function performanceDelta(current, previous) {
  if (!previous) return "new";
  const c = performanceValues(current), p = performanceValues(previous);
  const result = Object.fromEntries(basePerformanceKeys.map(key => [key, performanceChange(c[key], p[key])]));
  for (const metric of Object.keys(operands)) result[metric] = performanceChange(finiteMetric(current[metric]), finiteMetric(previous[metric]), ["ctr", "cvr", "roas"].includes(metric));
  return result;
}

// Preserve the established daily NEW label only for a measured-zero base.
export function dailyComparisonChange(current, previous, points = false) {
  if (!points && previous === 0 && Number.isFinite(current) && current > 0) return "new";
  return performanceChange(current, previous, points);
}
